import React, { useState } from 'react';
import {
  Search, Filter, Download, ChevronLeft, ChevronRight,
  Eye, User, Mail, Phone, Calendar, Star,
  ShoppingBag, DollarSign, TrendingUp, Clock,
  CheckCircle, XCircle, AlertCircle, MapPin,
  MoreVertical, Users as UsersIcon, UserCheck,
  UserX, Activity, Award, Crown, Sparkles, Store
} from 'lucide-react';
import { Link } from 'react-router-dom';

const PartnerUsers = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const itemsPerPage = 6;

  // Static Mock Data
  const users = [
    {
      id: 'USR-001',
      name: 'Rahul Sharma',
      email: 'rahul@example.com',
      phone: '+91 98765 43210',
      role: 'Customer',
      status: 'Active',
      joinDate: 'Jan 15, 2024',
      lastActive: 'Today, 10:30 AM',
      orders: 12,
      spent: 24500,
      rating: 4.8,
      avatar: 'R',
      location: 'New Delhi, India',
      verified: true
    },
    {
      id: 'USR-002',
      name: 'Priya Patel',
      email: 'priya@example.com',
      phone: '+91 98765 43211',
      role: 'Customer',
      status: 'Active',
      joinDate: 'Mar 22, 2024',
      lastActive: 'Yesterday, 3:15 PM',
      orders: 8,
      spent: 12800,
      rating: 4.5,
      avatar: 'P',
      location: 'Mumbai, India',
      verified: true
    },
    {
      id: 'USR-003',
      name: 'Green Thumb Nursery',
      email: 'contact@greenthumb.com',
      phone: '+91 98765 43212',
      role: 'Seller',
      status: 'Active',
      joinDate: 'Dec 05, 2023',
      lastActive: '2 hours ago',
      orders: 245,
      spent: 458900,
      rating: 4.9,
      avatar: 'G',
      location: 'Bengaluru, India',
      verified: true,
      storeName: 'Green Thumb Nursery'
    },
    {
      id: 'USR-004',
      name: 'Sneha Gupta',
      email: 'sneha@example.com',
      phone: '+91 98765 43213',
      role: 'Customer',
      status: 'Inactive',
      joinDate: 'Oct 01, 2024',
      lastActive: '2 weeks ago',
      orders: 3,
      spent: 2850,
      rating: 3.8,
      avatar: 'S',
      location: 'Pune, India',
      verified: false
    },
    {
      id: 'USR-005',
      name: 'Flora Bliss',
      email: 'hello@florabliss.com',
      phone: '+91 98765 43214',
      role: 'Seller',
      status: 'Active',
      joinDate: 'Jun 18, 2024',
      lastActive: 'Today, 8:15 AM',
      orders: 89,
      spent: 178500,
      rating: 4.7,
      avatar: 'F',
      location: 'Chennai, India',
      verified: true,
      storeName: 'Flora Bliss'
    },
    {
      id: 'USR-006',
      name: 'Amit Singh',
      email: 'amit@example.com',
      phone: '+91 98765 43215',
      role: 'Customer',
      status: 'Active',
      joinDate: 'Aug 10, 2024',
      lastActive: 'Yesterday, 6:20 PM',
      orders: 15,
      spent: 32100,
      rating: 4.6,
      avatar: 'A',
      location: 'Hyderabad, India',
      verified: true
    },
    {
      id: 'USR-007',
      name: 'Herbals Co.',
      email: 'info@herbals.com',
      phone: '+91 98765 43216',
      role: 'Seller',
      status: 'Pending',
      joinDate: 'Oct 20, 2024',
      lastActive: '1 day ago',
      orders: 0,
      spent: 0,
      rating: 0,
      avatar: 'H',
      location: 'Jaipur, India',
      verified: false,
      storeName: 'Herbals Co.'
    },
  ];

  const stats = {
    total: users.length,
    customers: users.filter(u => u.role === 'Customer').length,
    sellers: users.filter(u => u.role === 'Seller').length,
    active: users.filter(u => u.status === 'Active').length,
    inactive: users.filter(u => u.status === 'Inactive').length,
    pending: users.filter(u => u.status === 'Pending').length,
    totalSpent: users.reduce((sum, u) => sum + u.spent, 0),
    totalOrders: users.reduce((sum, u) => sum + u.orders, 0)
  };

  const getRoleConfig = (role) => {
    switch (role) {
      case 'Customer': return { icon: User, color: '#10b981', bg: '#d1fae5' };
      case 'Seller': return { icon: Store, color: '#3b82f6', bg: '#dbeafe' };
      default: return { icon: User, color: '#64748b', bg: '#f1f5f9' };
    }
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'Active': return { icon: CheckCircle, color: '#10b981', bg: '#d1fae5' };
      case 'Inactive': return { icon: XCircle, color: '#ef4444', bg: '#fee2e2' };
      case 'Pending': return { icon: Clock, color: '#f59e0b', bg: '#fed7aa' };
      default: return { icon: AlertCircle, color: '#64748b', bg: '#f1f5f9' };
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    const matchesStatus = filterStatus === 'all' || user.status === filterStatus;
    return matchesSearch && matchesRole && matchesStatus;
  });

  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const paginatedUsers = filteredUsers.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const StatCard = ({ title, value, icon: Icon, color, bgColor }) => (
    <div className="stat-card">
      <div className="stat-icon" style={{ backgroundColor: bgColor }}>
        <Icon size={20} color={color} />
      </div>
      <div className="stat-info">
        <h3>{value}</h3>
        <span>{title}</span>
      </div>
    </div>
  );

  const UserCard = ({ user }) => {
    const roleConfig = getRoleConfig(user.role);
    const statusConfig = getStatusConfig(user.status);
    const RoleIcon = roleConfig.icon;
    const StatusIcon = statusConfig.icon;
    
    return (
      <div className="user-card">
        <div className="user-card-header">
          <div className="user-avatar">
            <span>{user.avatar}</span>
            {user.verified && <div className="verified-badge">✓</div>}
          </div>
          <div className="user-info">
            <h4 className="user-name">{user.name}</h4>
            {user.storeName && <p className="store-name">{user.storeName}</p>}
            <div className="user-badges">
              <span className="role-badge" style={{ backgroundColor: roleConfig.bg, color: roleConfig.color }}>
                <RoleIcon size={12} />
                {user.role}
              </span>
              <span className="status-badge" style={{ backgroundColor: statusConfig.bg, color: statusConfig.color }}>
                <StatusIcon size={12} />
                {user.status}
              </span>
            </div>
          </div>
        </div>
        <div className="user-card-body">
          <div className="user-contact">
            <div className="contact-item">
              <Mail size={14} />
              <span>{user.email}</span>
            </div>
            <div className="contact-item">
              <Phone size={14} />
              <span>{user.phone}</span>
            </div>
            <div className="contact-item">
              <MapPin size={14} />
              <span>{user.location}</span>
            </div>
          </div>
          <div className="user-stats">
            <div className="stat-item">
              <ShoppingBag size={14} />
              <div>
                <span className="stat-value">{user.orders}</span>
                <span className="stat-label">Orders</span>
              </div>
            </div>
            <div className="stat-item">
              <DollarSign size={14} />
              <div>
                <span className="stat-value">₹{user.spent.toLocaleString()}</span>
                <span className="stat-label">Spent</span>
              </div>
            </div>
            <div className="stat-item">
              <Star size={14} />
              <div>
                <span className="stat-value">{user.rating}</span>
                <span className="stat-label">Rating</span>
              </div>
            </div>
          </div>
          <div className="user-footer">
            <div className="join-date">
              <Calendar size={12} />
              <span>Joined {user.joinDate}</span>
            </div>
            <button className="view-details-btn" onClick={() => {
              setSelectedUser(user);
              setShowDetailsModal(true);
            }}>
              View Details →
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="partner-users-modern">
      {/* Header */}
      <div className="users-header">
        <div>
          <h1 className="page-title">Users Management</h1>
          <p className="page-subtitle">View all customers and sellers on the platform</p>
        </div>
        <div className="header-actions">
          <button className="btn-export">
            <Download size={16} />
            Export Users
          </button>
        </div>
      </div>

      {/* Read-Only Banner */}
      <div className="readonly-banner">
        <Eye size={18} />
        <span>You have read-only access. You can view user data but cannot make changes.</span>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <StatCard title="Total Users" value={stats.total} icon={UsersIcon} color="#10b981" bgColor="#d1fae5" />
        <StatCard title="Customers" value={stats.customers} icon={User} color="#3b82f6" bgColor="#dbeafe" />
        <StatCard title="Sellers" value={stats.sellers} icon={Store} color="#8b5cf6" bgColor="#e9d5ff" />
        <StatCard title="Active" value={stats.active} icon={UserCheck} color="#10b981" bgColor="#d1fae5" />
        <StatCard title="Total Spent" value={`₹${stats.totalSpent.toLocaleString()}`} icon={DollarSign} color="#f59e0b" bgColor="#fed7aa" />
        <StatCard title="Total Orders" value={stats.totalOrders} icon={ShoppingBag} color="#06b6d4" bgColor="#cffafe" />
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <div className="search-box">
          <Search size={18} />
          <input 
            type="text" 
            placeholder="Search by name or email..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <select className="filter-select" value={filterRole} onChange={(e) => setFilterRole(e.target.value)}>
            <option value="all">All Roles</option>
            <option value="Customer">Customers</option>
            <option value="Seller">Sellers</option>
          </select>
          <select className="filter-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">All Status</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
            <option value="Pending">Pending</option>
          </select>
        </div>
      </div>

      {/* Results Info */}
      <div className="results-info">
        <span>{filteredUsers.length} Users Found</span>
      </div>

      {/* Users Grid */}
      <div className="users-grid">
        {paginatedUsers.map((user) => (
          <UserCard key={user.id} user={user} />
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination-wrapper">
          <div className="pagination-info">
            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredUsers.length)} of {filteredUsers.length} users
          </div>
          <div className="pagination-buttons">
            <button 
              className={`page-btn ${currentPage === 1 ? 'disabled' : ''}`} 
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft size={16} /> Previous
            </button>
            {[...Array(totalPages)].map((_, i) => (
              <button 
                key={i}
                className={`page-number ${currentPage === i + 1 ? 'active' : ''}`}
                onClick={() => setCurrentPage(i + 1)}
              >
                {i + 1}
              </button>
            ))}
            <button 
              className={`page-btn ${currentPage === totalPages ? 'disabled' : ''}`} 
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              Next <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Empty State */}
      {filteredUsers.length === 0 && (
        <div className="empty-state">
          <UsersIcon size={64} className="empty-icon" />
          <h3>No users found</h3>
          <p>Try adjusting your search or filter criteria</p>
        </div>
      )}

      {/* User Details Modal */}
      {showDetailsModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowDetailsModal(false)}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-header-info">
                <div className="modal-avatar">
                  <span>{selectedUser.avatar}</span>
                </div>
                <div>
                  <h3>{selectedUser.name}</h3>
                  <p className="modal-id">{selectedUser.id}</p>
                </div>
              </div>
              <button className="modal-close" onClick={() => setShowDetailsModal(false)}>×</button>
            </div>

            <div className="modal-body">
              <div className="detail-section">
                <h4>Contact Information</h4>
                <div className="info-grid">
                  <div className="info-item">
                    <Mail size={14} />
                    <span>{selectedUser.email}</span>
                  </div>
                  <div className="info-item">
                    <Phone size={14} />
                    <span>{selectedUser.phone}</span>
                  </div>
                  <div className="info-item">
                    <MapPin size={14} />
                    <span>{selectedUser.location}</span>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h4>Account Information</h4>
                <div className="info-grid">
                  <div className="info-item">
                    <Calendar size={14} />
                    <span>Joined: {selectedUser.joinDate}</span>
                  </div>
                  <div className="info-item">
                    <Clock size={14} />
                    <span>Last Active: {selectedUser.lastActive}</span>
                  </div>
                  <div className="info-item">
                    <Award size={14} />
                    <span>Role: {selectedUser.role}</span>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h4>Activity Summary</h4>
                <div className="stats-grid-modal">
                  <div className="modal-stat">
                    <ShoppingBag size={18} />
                    <div>
                      <div className="modal-stat-value">{selectedUser.orders}</div>
                      <div className="modal-stat-label">Total Orders</div>
                    </div>
                  </div>
                  <div className="modal-stat">
                    <DollarSign size={18} />
                    <div>
                      <div className="modal-stat-value">₹{selectedUser.spent.toLocaleString()}</div>
                      <div className="modal-stat-label">Total Spent</div>
                    </div>
                  </div>
                  <div className="modal-stat">
                    <Star size={18} />
                    <div>
                      <div className="modal-stat-value">{selectedUser.rating}</div>
                      <div className="modal-stat-label">Rating</div>
                    </div>
                  </div>
                </div>
              </div>

              {selectedUser.role === 'Seller' && selectedUser.storeName && (
                <div className="detail-section">
                  <h4>Store Information</h4>
                  <div className="info-item">
                    <Store size={14} />
                    <span>{selectedUser.storeName}</span>
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowDetailsModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
      <style>{`
        .partner-users-modern {
          padding: 24px;
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          min-height: 100vh;
        }

        /* Header */
        .users-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 28px;
          flex-wrap: wrap;
          gap: 16px;
        }

        .page-title {
          font-size: 28px;
          font-weight: 700;
          background: linear-gradient(135deg, #1a472a 0%, #2d6a4f 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          margin-bottom: 4px;
        }

        .page-subtitle {
          color: #64748b;
          font-size: 14px;
          margin: 0;
        }

        .header-actions {
          display: flex;
          gap: 12px;
        }

        .btn-export {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 20px;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 40px;
          font-size: 13px;
          cursor: pointer;
        }

        .btn-export:hover {
          border-color: #10b981;
          color: #10b981;
        }

        /* Read-Only Banner */
        .readonly-banner {
          background: #fef3c7;
          border: 1px solid #fde68a;
          border-radius: 12px;
          padding: 12px 20px;
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 24px;
          color: #92400e;
          font-size: 13px;
        }

        /* Stats Grid */
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(6, 1fr);
          gap: 16px;
          margin-bottom: 28px;
        }

        .stat-card {
          background: white;
          border-radius: 16px;
          padding: 16px;
          display: flex;
          align-items: center;
          gap: 12px;
          border: 1px solid #e8ecef;
          transition: all 0.3s ease;
        }

        .stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(0,0,0,0.08);
        }

        .stat-icon {
          width: 48px;
          height: 48px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 14px;
        }

        .stat-info h3 {
          font-size: 20px;
          font-weight: 700;
          color: #1e293b;
          margin: 0;
        }

        .stat-info span {
          font-size: 11px;
          color: #64748b;
        }

        /* Filters */
        .filters-bar {
          background: white;
          border-radius: 16px;
          padding: 12px 20px;
          margin-bottom: 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 12px;
          border: 1px solid #e8ecef;
        }

        .search-box {
          position: relative;
          flex: 1;
          max-width: 320px;
        }

        .search-box svg {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: #94a3b8;
        }

        .search-box input {
          width: 100%;
          padding: 8px 16px 8px 38px;
          border: 1px solid #e2e8f0;
          border-radius: 40px;
          font-size: 13px;
        }

        .filter-group {
          display: flex;
          gap: 12px;
        }

        .filter-select {
          padding: 6px 16px;
          border: 1px solid #e2e8f0;
          border-radius: 40px;
          font-size: 13px;
          background: white;
        }

        /* Results Info */
        .results-info {
          margin-bottom: 20px;
          font-size: 13px;
          color: #64748b;
        }

        /* Users Grid */
        .users-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
          gap: 24px;
          margin-bottom: 24px;
        }

        .user-card {
          background: white;
          border-radius: 20px;
          overflow: hidden;
          transition: all 0.3s ease;
          border: 1px solid #e8ecef;
        }

        .user-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 24px rgba(0,0,0,0.1);
        }

        .user-card-header {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 20px;
          background: #f8fafc;
          border-bottom: 1px solid #e8ecef;
        }

        .user-avatar {
          position: relative;
          width: 64px;
          height: 64px;
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 24px;
          font-weight: 600;
        }

        .verified-badge {
          position: absolute;
          bottom: 0;
          right: 0;
          width: 20px;
          height: 20px;
          background: #10b981;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          color: white;
          border: 2px solid white;
        }

        .user-name {
          font-size: 16px;
          font-weight: 600;
          color: #1e293b;
          margin: 0 0 4px;
        }

        .store-name {
          font-size: 12px;
          color: #10b981;
          margin: 0 0 8px;
        }

        .user-badges {
          display: flex;
          gap: 8px;
        }

        .role-badge, .status-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 3px 8px;
          border-radius: 20px;
          font-size: 10px;
          font-weight: 500;
        }

        .user-card-body {
          padding: 16px 20px;
        }

        .user-contact {
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin-bottom: 16px;
        }

        .contact-item {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 12px;
          color: #64748b;
        }

        .user-stats {
          display: flex;
          justify-content: space-between;
          padding: 12px 0;
          border-top: 1px solid #e8ecef;
          border-bottom: 1px solid #e8ecef;
          margin-bottom: 12px;
        }

        .stat-item {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .stat-item div {
          display: flex;
          flex-direction: column;
        }

        .stat-value {
          font-size: 14px;
          font-weight: 700;
          color: #1e293b;
        }

        .stat-label {
          font-size: 10px;
          color: #64748b;
        }

        .user-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .join-date {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 11px;
          color: #94a3b8;
        }

        .view-details-btn {
          background: none;
          border: none;
          color: #10b981;
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
        }

        /* Pagination */
        .pagination-wrapper {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 0;
          flex-wrap: wrap;
          gap: 16px;
          border-top: 1px solid #e8ecef;
        }

        .pagination-info {
          font-size: 13px;
          color: #64748b;
        }

        .pagination-buttons {
          display: flex;
          gap: 8px;
          align-items: center;
        }

        .page-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 14px;
          border: 1px solid #e2e8f0;
          background: white;
          border-radius: 8px;
          font-size: 13px;
          cursor: pointer;
        }

        .page-btn.disabled, .page-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .page-number {
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 1px solid #e2e8f0;
          background: white;
          border-radius: 8px;
          cursor: pointer;
          font-size: 13px;
        }

        .page-number.active {
          background: #10b981;
          border-color: #10b981;
          color: white;
        }

        /* Empty State */
        .empty-state {
          text-align: center;
          padding: 60px 20px;
          background: white;
          border-radius: 20px;
        }

        .empty-icon {
          color: #94a3b8;
          margin-bottom: 16px;
        }

        .empty-state h3 {
          font-size: 18px;
          font-weight: 600;
          color: #1e293b;
          margin: 0 0 8px;
        }

        .empty-state p {
          font-size: 13px;
          color: #64748b;
          margin: 0;
        }

        /* Modal */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          backdrop-filter: blur(4px);
        }

        .modal-container {
          background: white;
          border-radius: 24px;
          width: 90%;
          max-width: 500px;
          max-height: 85vh;
          overflow-y: auto;
          animation: modalSlideIn 0.3s ease;
        }

        @keyframes modalSlideIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          border-bottom: 1px solid #e8ecef;
        }

        .modal-header-info {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .modal-avatar {
          width: 56px;
          height: 56px;
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 24px;
          font-weight: 600;
        }

        .modal-header h3 {
          font-size: 18px;
          font-weight: 600;
          color: #1e293b;
          margin: 0;
        }

        .modal-id {
          font-size: 11px;
          color: #94a3b8;
          margin-top: 4px;
        }

        .modal-close {
          width: 32px;
          height: 32px;
          background: #f1f5f9;
          border: none;
          border-radius: 8px;
          font-size: 20px;
          cursor: pointer;
        }

        .modal-body {
          padding: 24px;
        }

        .detail-section {
          margin-bottom: 24px;
        }

        .detail-section h4 {
          font-size: 14px;
          font-weight: 600;
          color: #1e293b;
          margin: 0 0 12px;
          padding-bottom: 8px;
          border-bottom: 1px solid #e8ecef;
        }

        .info-grid {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .info-item {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 13px;
          color: #475569;
        }

        .stats-grid-modal {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
        }

        .modal-stat {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px;
          background: #f8fafc;
          border-radius: 12px;
        }

        .modal-stat-value {
          font-size: 16px;
          font-weight: 700;
          color: #1e293b;
        }

        .modal-stat-label {
          font-size: 10px;
          color: #64748b;
        }

        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          padding: 16px 24px;
          border-top: 1px solid #e8ecef;
        }

        .btn-secondary {
          padding: 8px 20px;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 40px;
          font-size: 13px;
          cursor: pointer;
        }

        /* Responsive */
        @media (max-width: 1200px) {
          .stats-grid {
            grid-template-columns: repeat(3, 1fr);
          }
          .users-grid {
            grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
          }
        }

        @media (max-width: 768px) {
          .partner-users-modern {
            padding: 16px;
          }
          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }
          .filters-bar {
            flex-direction: column;
            align-items: stretch;
          }
          .search-box {
            max-width: none;
          }
          .users-grid {
            grid-template-columns: 1fr;
          }
          .stats-grid-modal {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default PartnerUsers;